/**
 * How a screen's picture fits the viewer, and when the host's display follows it (`vres`, docs/crd-hardening/
 * display-resolution.md). Pure: the rules are ported from the Windows viewer (content-platform-crd-win-native
 * web/input.mjs, vresCommand / vresOnFollowChange) and keep its tests (crd-display.spec.ts).
 */

/**
 * - `fit` (the default): the host keeps its own size (`vres off`), scaled up or down to the area, aspect ratio kept.
 * - `follow`: the host's display takes the video area's size (`vres <w> <h>`). The host can only pick a listed mode,
 *   usually a little smaller than asked: shown 1:1 and centred, scaled down only when larger than the area.
 * - `actual`: the host keeps its own size (`vres off`), shown 1:1; scroll bars when larger than the area.
 *
 * 1:1 is one host pixel per CSS pixel, like the size `vres` asks for (logical pixels, decision 2026-10-18).
 */
export type CrdDisplayMode = 'follow' | 'actual' | 'fit';

export const CRD_DISPLAY_MODES: readonly CrdDisplayMode[] = ['follow', 'actual', 'fit'];

export interface Size {
  w: number;
  h: number;
}

/**
 * Whether to ask the host for the video area now, with the size last sent on this channel. `follow`: the mode is
 * `follow`; `area` the video area in CSS pixels (null before the first layout, or while the screen is hidden);
 * `sent` what this channel last asked for (null: nothing yet). The command, or null: not following, no area, an
 * empty one, or the same size again.
 */
export function vresCommand(follow: boolean, area: Size | null, sent: Size | null): string | null {
  if (!follow || !area || area.w <= 0 || area.h <= 0) return null;
  if (sent && sent.w === area.w && sent.h === area.h) return null;
  return `vres ${area.w} ${area.h}`;
}

/**
 * Following was turned from `wasOn` to `on`, with the data channel open or not: the commands to send now and the
 * new `sent`. Off: `vres off` (the host goes back to its saved mode at once; harmless when it never changed it), and
 * nothing is remembered as sent. On: the current size at once, even the size asked before (the host has gone back
 * since). Without an open channel nothing is sent; the next channel follows the mode.
 */
export function vresOnFollowChange(wasOn: boolean, on: boolean, area: Size | null, sent: Size | null,
                                   channelOpen: boolean): {commands: string[]; sent: Size | null} {
  if (wasOn === on) return {commands: [], sent};
  if (!on) return {commands: channelOpen ? ['vres off'] : [], sent: null};
  const cmd = channelOpen ? vresCommand(true, area, null) : null;
  return {commands: cmd ? [cmd] : [], sent: cmd ? {w: area!.w, h: area!.h} : null};
}

/** The picture's size on the page in CSS pixels, for a stream of `video` in a video area of `box`; null before both are known. */
export function pictureSize(mode: CrdDisplayMode, video: Size, box: Size): Size | null {
  if (video.w <= 0 || video.h <= 0) return null;
  if (mode === 'actual') return {w: video.w, h: video.h};
  if (box.w <= 0 || box.h <= 0) return null;
  // the limiting side takes the area's size exactly (input.mjs contentRect)
  const wide = box.w * video.h > box.h * video.w;   // bars left and right
  const fitted = wide ? {w: video.w * box.h / video.h, h: box.h} : {w: box.w, h: video.h * box.w / video.w};
  return mode === 'follow' && fitted.w > video.w ? {w: video.w, h: video.h} : fitted;
}

/**
 * A point on the picture → host pixel, +1 like the other viewers (the binary offsets by its monitor's rect and
 * clamps). `x`/`y` relative to the picture's top left, `shown` the picture's size on the page.
 */
export function hostPoint(x: number, y: number, video: Size, shown: Size): {x: number; y: number} {
  if (video.w <= 0 || shown.w <= 0 || shown.h <= 0) return {x: 0, y: 0};
  return {x: Math.round(x * video.w / shown.w) + 1, y: Math.round(y * video.h / shown.h) + 1};
}

/** localStorage key of the mode, per browser: every screen and window shares it. */
export const CRD_DISPLAY_MODE_KEY = 'crd-display-mode';

/** The stored mode; `fit` (scale to window) when none (or not one of the modes) is stored. */
export function parseDisplayMode(stored: string | null): CrdDisplayMode {
  return (CRD_DISPLAY_MODES as readonly string[]).includes(stored ?? '') ? stored as CrdDisplayMode : 'fit';
}
